/**
 * useFavorites (NEW) - manages the user's saved-videos feed.
 *
 * Favorites are persisted as self-contained snapshots so the Favorites tab can
 * play them without re-fetching the source channel. Toggling emits a
 * `video_favorite` analytics event mirroring the existing `video_reaction`.
 */
import { useState, useCallback, useEffect, useRef } from 'preact/hooks';
import {
  loadFavorites,
  saveFavorites,
  toggleFavorite as toggleFav,
  isFavorited,
} from '../lib/favorites.js';
import { sendEvent, Events } from '../bridge/events.js';

/**
 * @param {'chill'|'nsfw'} scope - channel kind; favorites are isolated per scope.
 */
export function useFavorites(scope) {
  const [favorites, setFavorites] = useState(() => loadFavorites(scope));
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  // Re-read the correct list whenever the active channel changes.
  useEffect(() => {
    setFavorites(loadFavorites(scope));
  }, [scope]);

  const isFavorite = useCallback((id) => isFavorited(favorites, id), [favorites]);

  /**
   * Toggle a favorite. `fav` is a snapshot built by the feed
   * (resolved playlist/poster/avatar + metadata + channel).
   */
  const toggle = useCallback((fav) => {
    setFavorites((prev) => {
      const { list, active } = toggleFav(prev, fav);
      saveFavorites(scopeRef.current, list);
      sendEvent(Events.VideoFavorite, {
        id: fav.id,
        active,
        content: fav.content,
      });
      return list;
    });
  }, []);

  /** Idempotent add (used by the auto-favorite-at-10-likes flow). */
  const add = useCallback((fav) => {
    setFavorites((prev) => {
      if (isFavorited(prev, fav.id)) return prev;
      const list = [{ ...fav, savedAt: Date.now() }, ...prev];
      saveFavorites(scopeRef.current, list);
      sendEvent(Events.VideoFavorite, {
        id: fav.id,
        active: true,
        content: fav.content,
      });
      return list;
    });
  }, []);

  const remove = useCallback((id) => {
    setFavorites((prev) => {
      const list = prev.filter((f) => f.id !== id);
      saveFavorites(scopeRef.current, list);
      return list;
    });
  }, []);

  return { favorites, isFavorite, toggle, add, remove };
}
